import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { AuthLayout } from "@/components/auth-layout";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import { SocialLogin } from "@/components/social-login";
import { Form, FormField, FormSubmit } from "@/src/forms";
import {
  login as authLogin,
  verifyLoginOtp,
  bootstrapSessionToken,
  isAuthenticated,
} from "@/src/services/auth_service";
import { captureEvent } from "@/src/lib/posthog";
import { loginSchema, otpSchema, type LoginValues, type OtpValues } from "./_schemas/login";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"credentials" | "otp">("credentials");
  const [otpHash, setOtpHash] = useState("");
  const [email, setEmail] = useState("");

  // ApiActivationView (sanctionsio API) redirects here with this flag when the
  // activation link's uid/token don't check out.
  const activationFailed = searchParams.get("activation_error") === "true";

  async function handleCredentials(values: LoginValues) {
    setError(null);
    const recaptchaToken = executeRecaptcha ? await executeRecaptcha("login") : undefined;
    const res = await authLogin(values.email, values.password, recaptchaToken);
    if (!res.ok) {
      setError(res.error.message);
      return;
    }
    setEmail(values.email);
    captureEvent("Logged", { method: "password" });
    if (res.value.kind === "otp_required") {
      setOtpHash(res.value.otpHash);
      setStep("otp");
      return;
    }
    await finishLogin();
  }

  async function handleOtp(values: OtpValues) {
    setError(null);
    const res = await verifyLoginOtp(otpHash, values.otp);
    if (!res.ok) {
      setError(res.error.message);
      return;
    }
    captureEvent("Logged OTP");
    await finishLogin();
  }

  // The auth POSTs establish a session but return no token — the bearer is read
  // from /info/.user.token. Same-site prod uses the session cookie; in dev the
  // seeded VITE_API_TOKEN already authenticates /info/, so isAuthenticated() is a
  // valid fallback. Once a bearer is present, the app shell bootstraps and routes.
  async function finishLogin() {
    const boot = await bootstrapSessionToken();
    if (!boot.ok && !isAuthenticated()) {
      // Credentials/OTP were already accepted to reach here — the failure is
      // reading the session token from /info/ (e.g. a 401 when the session can't
      // be established cross-site). The raw DRF "Authentication credentials were
      // not provided" reads as a wrong-password error, which is misleading; show
      // an actionable, plain message instead.
      setError(
        "We couldn't finish signing you in. Please try again, or contact support if this keeps happening.",
      );
      return;
    }
    navigate("/", { replace: true });
  }

  function backToCredentials() {
    setError(null);
    setOtpHash("");
    setStep("credentials");
  }

  if (activationFailed) {
    return (
      <AuthLayout>
        <div className="popup w-100 rounded-lg">
          <div className="flex flex-col gap-4">
            <Icon name="error" size={40} className="text-red-600" label="Error" />
            <h1 className="text-h4 text-black">We couldn&apos;t activate your account</h1>
            <p className="text-small text-grey-900">
              Something went wrong activating your account. Please reset your password and try
              logging in — if the problem continues, contact support.
            </p>
            <Button hierarchy="primary" size="md" onClick={() => navigate("/forgot-password")}>
              Reset password
            </Button>
            <Link
              to="/login"
              className="text-center text-small font-medium text-green-700 hover:text-green-600"
            >
              Try logging in
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="popup w-100 rounded-lg">
        {step === "credentials" ? (
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-2">
              <h1 className="text-h4 text-black">Welcome back</h1>
              <p className="text-small text-grey-900">Enter your email to log in.</p>
            </div>

            <Form
              schema={loginSchema}
              defaultValues={{ email: "", password: "" }}
              onSubmit={handleCredentials}
              className="flex flex-col gap-4"
            >
              <FormField
                name="email"
                label="Email"
                type="email"
                autoComplete="email"
                placeholder="username@example.com"
              />

              <div className="flex w-full flex-col gap-1">
                <FormField
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••••"
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="flex"
                    >
                      <Icon name={showPassword ? "visibility_off" : "visibility"} size={20} />
                    </button>
                  }
                />
                <p className="text-small text-grey-900">
                  Forgot your password?{" "}
                  <Link
                    to="/forgot-password"
                    className="font-medium text-green-700 hover:text-green-600"
                  >
                    Click here
                  </Link>
                </p>
              </div>

              {error && (
                <p
                  role="alert"
                  className="rounded-sm border border-red-500 bg-red-100 px-3 py-2 text-small text-red-600"
                >
                  {error}
                </p>
              )}

              <FormSubmit
                hierarchy="primary"
                size="md"
                className="w-full"
                pendingLabel="Signing in…"
                rightIcon={<Icon name="arrow_forward" size={20} />}
              >
                Login
              </FormSubmit>

              <Divider label="or" />

              <SocialLogin />
            </Form>

            <div className="flex items-center justify-center gap-1.5">
              <p className="text-small text-grey-900">Don&apos;t have an account?</p>
              <Link
                to="/signup"
                className="text-small font-medium text-green-700 hover:text-green-600"
              >
                Sign up
              </Link>
            </div>

            <p className="text-center text-small text-grey-500">
              This site is protected by reCAPTCHA and the Google{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-grey-700"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-grey-700"
              >
                Terms of Service
              </a>{" "}
              apply.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-2">
              <h1 className="text-h4 text-black">Enter your code</h1>
              <p className="text-small text-grey-900">
                We emailed a one-time code to{" "}
                <span className="font-medium text-black">{email}</span>.
              </p>
            </div>

            <Form
              schema={otpSchema}
              defaultValues={{ otp: "" }}
              onSubmit={handleOtp}
              className="flex flex-col gap-4"
            >
              <FormField
                name="otp"
                label="Verification code"
                autoComplete="one-time-code"
                placeholder="123456"
              />

              {error && (
                <p
                  role="alert"
                  className="rounded-sm border border-red-500 bg-red-100 px-3 py-2 text-small text-red-600"
                >
                  {error}
                </p>
              )}

              <FormSubmit
                hierarchy="primary"
                size="md"
                className="w-full"
                pendingLabel="Verifying…"
                rightIcon={<Icon name="arrow_forward" size={20} />}
              >
                Verify and sign in
              </FormSubmit>

              <button
                type="button"
                onClick={backToCredentials}
                className="text-small font-medium text-green-700 hover:text-green-600"
              >
                Back to login
              </button>
            </Form>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2">
      <div className="h-px flex-1 bg-grey-200" />
      <span className="text-small text-grey-900">{label}</span>
      <div className="h-px flex-1 bg-grey-200" />
    </div>
  );
}
